// Режим размышления модели — переключатель для сравнения двух режимов на одних и
// тех же вопросах.
//
// Настройка живёт в localStorage, а не в состоянии страницы: чат открыт в трёх
// местах (страница чата, сайдбар карточки сделки, карточка контакта), и режим
// должен быть один на все. На сервер её не тащим — это инструмент разработки,
// а не свойство рабочего пространства.
//
// Читается в момент отправки, поэтому переключение действует со следующего
// сообщения и не требует пересоздавать агента.
import { useCallback, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'govard.chat.reasoning';
const memoryStorage: Record<string, string> = {};

const listeners = new Set<() => void>();

function readStorage(): boolean {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const val = window.localStorage.getItem(STORAGE_KEY);
      if (val !== null) return val !== 'off';
    }
  } catch {
    // fallback
  }
  return (memoryStorage[STORAGE_KEY] ?? 'on') !== 'off';
}

/** Размышляет ли модель сейчас. По умолчанию да — штатный режим провайдера. */
export function isReasoningEnabled(): boolean {
  return readStorage();
}

export function setReasoningEnabled(enabled: boolean): void {
  const value = enabled ? 'on' : 'off';
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(STORAGE_KEY, value);
    }
  } catch {
    // fallback
  }
  memoryStorage[STORAGE_KEY] = value;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // Вторая вкладка тоже меняет настройку — событие storage приходит только туда,
  // где запись не происходила, поэтому свой список слушателей всё равно нужен.
  const onStorage = (event: StorageEvent): void => {
    if (event.key === STORAGE_KEY) listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

export function useReasoningMode(): {
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
} {
  const enabled = useSyncExternalStore(subscribe, readStorage, () => true);
  const setEnabled = useCallback((next: boolean) => setReasoningEnabled(next), []);
  return { enabled, setEnabled };
}
